<template>
    <div class="w-100 align-self-start">
        <v-card flat color="transparent">
            <v-card-title>
                <span class="secondary--text">Resource sizes</span>
            </v-card-title>
            <v-card-text>
                <div class="d-flex align-center">
                    <v-btn color="primary" class="mr-2 d-none" @click="openCreateDialog">
                        <v-icon left>mdi-plus</v-icon>
                        Resource
                    </v-btn>
                    <v-menu offset-y :disabled="refreshingResources || loadingIntegrations || integrations.length === 0">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn
                                v-bind="attrs"
                                v-on="on"
                                color="primary"
                                class="mr-2"
                                :loading="refreshingResources"
                                :disabled="loadingIntegrations || integrations.length === 0">
                                <v-icon left>mdi-refresh</v-icon>
                                Refresh resources
                                <v-icon right>mdi-chevron-down</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <v-list-item v-for="integration in integrations" :key="integration.tcid" @click="refreshIntegrationResources(integration)">
                                <v-list-item-content>
                                    <v-list-item-title>{{ integrationLabel(integration) }}</v-list-item-title>
                                    <v-list-item-subtitle>{{ integrationDetails(integration) }}</v-list-item-subtitle>
                                </v-list-item-content>
                            </v-list-item>
                        </v-list>
                    </v-menu>

                    <v-spacer></v-spacer>

                    <v-text-field
                        v-model="search"
                        prepend-inner-icon="mdi-magnify "
                        autofocus
                        hide-details
                        solo
                        flat
                        dense
                        clearable
                        placeholder="Search for a resource..."
                        class="mr-2">
                        <template v-slot:append>
                            <v-menu offset-y :close-on-content-click="false">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                        <v-icon left>mdi-tune-variant</v-icon>
                                        <span>Filters</span>
                                    </v-btn>
                                </template>
                                <v-card flat class="pa-3" min-width="720">
                                    <v-row>
                                        <v-col cols="12" md="4" class="node-pool-filter-section">
                                            <v-subheader class="text-uppercase">Provider</v-subheader>
                                            <template v-if="cloudFilterOptions.length > 0">
                                                <div v-for="cloud in cloudFilterOptions" :key="`cloud-filter-${cloud}`">
                                                    <v-sheet rounded class="py-2 px-4 my-1">
                                                        <v-switch
                                                            inset
                                                            dense
                                                            hide-details
                                                            class="pa-0 ma-0"
                                                            :input-value="activeSelectedClouds.includes(cloud)"
                                                            @change="setCloudSelection(cloud, $event)">
                                                            <template v-slot:label>
                                                                <span>{{ cloud }}</span>
                                                            </template>
                                                        </v-switch>
                                                    </v-sheet>
                                                </div>
                                            </template>
                                            <v-sheet v-else rounded class="py-2 px-4 my-1">
                                                <span class="caption">No provider values available.</span>
                                            </v-sheet>
                                        </v-col>

                                        <v-col cols="12" md="4" class="node-pool-filter-section node-pool-filter-section--divider">
                                            <v-subheader class="text-uppercase">Region</v-subheader>
                                            <template v-if="regionFilterOptions.length > 0">
                                                <div v-for="region in regionFilterOptions" :key="`region-filter-${region}`">
                                                    <v-sheet rounded class="py-2 px-4 my-1">
                                                        <v-switch
                                                            inset
                                                            dense
                                                            hide-details
                                                            class="pa-0 ma-0"
                                                            :input-value="activeSelectedRegions.includes(region)"
                                                            @change="setRegionSelection(region, $event)">
                                                            <template v-slot:label>
                                                                <span>{{ region }}</span>
                                                            </template>
                                                        </v-switch>
                                                    </v-sheet>
                                                </div>
                                            </template>
                                            <v-sheet v-else rounded class="py-2 px-4 my-1">
                                                <span class="caption">No region values available.</span>
                                            </v-sheet>
                                        </v-col>

                                        <v-col cols="12" md="4" class="node-pool-filter-section node-pool-filter-section--divider">
                                            <v-subheader class="text-uppercase">Enabled</v-subheader>
                                            <div v-for="enabledOption in enabledFilterOptions" :key="`enabled-filter-${enabledOption.label}`">
                                                <v-sheet rounded class="py-2 px-4 my-1">
                                                    <v-switch
                                                        inset
                                                        dense
                                                        hide-details
                                                        class="pa-0 ma-0"
                                                        :input-value="activeSelectedEnabledStates.includes(enabledOption.value)"
                                                        @change="setEnabledSelection(enabledOption.value, $event)">
                                                        <template v-slot:label>
                                                            <span>{{ enabledOption.label }}</span>
                                                        </template>
                                                    </v-switch>
                                                </v-sheet>
                                            </div>
                                        </v-col>
                                    </v-row>
                                </v-card>
                            </v-menu>
                        </template>
                    </v-text-field>
                    <v-btn icon :loading="loading" @click="fetchNodePools">
                        <v-icon>refresh</v-icon>
                    </v-btn>
                </div>

                <v-alert v-if="error" type="error" text dismissible class="mt-4 mb-0" @input="error = ''">{{ error }}</v-alert>
                <v-data-table
                    :headers="headers"
                    :items="filteredNodePools"
                    :search="search"
                    :loading="loading"
                    :items-per-page="25"
                    :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
                    class="mt-4"
                    no-data-text="No resources are configured for this resource pool.">
                    <template v-slot:[`item.enabled`]="{ item }">
                        <v-switch
                            :input-value="!item.disabled_override"
                            @change="setEnabled(item, Boolean($event))"
                            inset
                            dense
                            hide-details
                            color="success"></v-switch>
                    </template>
                    <template v-slot:[`item.cpu`]="{ item }">
                        <span>{{ item?.limits?.cpu || '-' }}</span>
                    </template>
                    <template v-slot:[`item.memory`]="{ item }">
                        <span>{{ item?.limits?.memory || '-' }}</span>
                    </template>
                    <template v-slot:[`item.price`]="{ item }">
                        <span>{{ nodePoolPriceAmount(item.short_id) }}</span>
                    </template>
                    <template v-slot:[`item.actions`]="{ item }">
                        <v-menu left offset-x nudge-top="10">
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" text color="secondary" small><v-icon>more_horiz</v-icon></v-btn>
                            </template>
                            <v-list nav dense>
                                <v-list-item @click="openEditDialog(item)">
                                    <v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon>
                                    <v-list-item-title class="mr-4">Edit</v-list-item-title>
                                </v-list-item>
                                <v-list-item @click="openPriceDialog(item)">
                                    <v-list-item-icon><v-icon>mdi-cash-edit</v-icon></v-list-item-icon>
                                    <v-list-item-title class="mr-4">Set price</v-list-item-title>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                    </template>
                </v-data-table>
            </v-card-text>
        </v-card>

        <v-dialog v-model="nodePoolDialog" max-width="760px" persistent>
            <v-card flat>
                <v-card-title class="flex-nowrap align-start">
                    <span class="secondary--text">{{ editingNodePool ? 'Edit resource' : 'Create resource' }}</span>
                    <v-spacer></v-spacer>
                    <v-btn icon :disabled="saving" @click="closeNodePoolDialog"><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-subtitle v-if="editingNodePool" class="pt-0">{{ nodePoolForm.long_id || '-' }} | {{ nodePoolForm.short_id || '-' }}</v-card-subtitle>
                <v-card-text>
                    <v-form ref="nodePoolForm" v-model="nodePoolValid">
                        <v-row v-if="!editingNodePool">
                            <v-col cols="12" md="6">
                                <v-text-field v-model="nodePoolForm.long_id" label="Name" :rules="[required]" outlined dense :disabled="saving"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field
                                    v-model="nodePoolForm.short_id"
                                    label="Short ID"
                                    :rules="[required]"
                                    outlined
                                    dense
                                    :disabled="saving"></v-text-field>
                            </v-col>
                        </v-row>
                        <v-textarea v-model="nodePoolForm.description" label="Description" rows="2" outlined dense :disabled="saving"></v-textarea>
                        <v-row v-if="!editingNodePool">
                            <v-col cols="12" md="4">
                                <v-select
                                    v-model="nodePoolForm.cloud"
                                    :items="cloudProviderOptions"
                                    item-text="display_label"
                                    item-value="cloud_provider"
                                    label="Provider"
                                    :rules="[required]"
                                    outlined
                                    dense
                                    :loading="loadingCloudProviders"
                                    :disabled="saving || loadingCloudProviders"
                                    no-data-text="No providers available."></v-select>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-select
                                    v-model="nodePoolForm.region"
                                    :items="cloudRegionOptions"
                                    label="Provider region"
                                    :rules="[required]"
                                    outlined
                                    dense
                                    :loading="loadingCloudProviders"
                                    :disabled="saving || loadingCloudProviders || !nodePoolForm.cloud"
                                    no-data-text="No regions available."></v-select>
                            </v-col>
                            <v-col cols="12" md="4">
                                <v-text-field v-model="nodePoolForm.cloud_sku" label="Provider SKU" outlined dense :disabled="saving"></v-text-field>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="nodePoolForm.cpu" label="CPU" :rules="[required]" outlined dense :disabled="saving"></v-text-field>
                            </v-col>
                            <v-col cols="12" md="6">
                                <v-text-field v-model="nodePoolForm.memory" label="Memory" :rules="[required]" outlined dense :disabled="saving"></v-text-field>
                            </v-col>
                        </v-row>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text :loading="saving" :disabled="!nodePoolValid" @click="saveNodePool">
                        {{ editingNodePool ? 'Save' : 'Create' }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog v-model="priceDialog" max-width="520px" persistent>
            <v-card flat>
                <v-card-title class="flex-nowrap align-start">
                    <span class="secondary--text">Set resource price</span>
                    <v-spacer></v-spacer>
                    <v-btn icon :disabled="savingPrice" @click="priceDialog = false"><v-icon>clear</v-icon></v-btn>
                </v-card-title>
                <v-card-subtitle>{{ price.long_id || '-' }} | {{ price.subresource || '-' }}</v-card-subtitle>
                <v-card-text>
                    <v-form ref="priceForm" v-model="priceValid">
                        <v-text-field
                            v-model.number="price.amount"
                            label="Price"
                            type="number"
                            min="0"
                            step="0.01"
                            :rules="[positive]"
                            outlined
                            dense
                            :disabled="savingPrice"></v-text-field>
                    </v-form>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" text :loading="savingPrice" :disabled="!priceValid" @click="savePrice">Save</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
export default {
    name: 'NodePools',
    data() {
        return {
            search: '',
            selectedClouds: null,
            selectedRegions: null,
            selectedEnabledStates: null,
            loading: false,
            loadingIntegrations: false,
            refreshingResources: false,
            loadingCloudProviders: false,
            saving: false,
            savingPrice: false,
            error: '',
            nodePools: [],
            prices: [],
            integrations: [],
            cloudProviders: [],
            nodePoolDialog: false,
            editingNodePool: null,
            nodePoolValid: false,
            priceDialog: false,
            priceValid: false,
            headers: [
                { text: 'Name', value: 'long_id' },
                { text: 'Short ID', value: 'short_id' },
                { text: 'Provider', value: 'cloud' },
                { text: 'Region', value: 'region' },
                { text: 'CPU', value: 'cpu' },
                { text: 'Memory', value: 'memory' },
                { text: 'Description', value: 'description' },
                { text: 'Price', value: 'price', sortable: false },
                { text: 'Enabled', value: 'enabled', sortable: false },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false }
            ],
            nodePoolForm: this.emptyNodePoolForm(),
            price: { long_id: '', subresource: '', amount: null }
        }
    },
    computed: {
        resourcePoolId() {
            return Number(this.$route.params.rpid)
        },
        cloudProviderOptions() {
            return this.cloudProviders
        },
        cloudRegionOptions() {
            if (!this.nodePoolForm.cloud) {
                return []
            }
            const selectedProvider = this.cloudProviders.find(provider => provider.cloud_provider === this.nodePoolForm.cloud)
            return selectedProvider?.regions || []
        },
        cloudFilterOptions() {
            const uniqueClouds = new Set(this.nodePools.map(nodePool => nodePool.cloud).filter(cloud => Boolean(cloud)))
            return Array.from(uniqueClouds).sort((a, b) => String(a).localeCompare(String(b)))
        },
        regionFilterOptions() {
            const uniqueRegions = new Set(this.nodePools.map(nodePool => nodePool.region).filter(region => Boolean(region)))
            return Array.from(uniqueRegions).sort((a, b) => String(a).localeCompare(String(b)))
        },
        enabledFilterOptions() {
            return [
                { label: 'Enabled', value: true },
                { label: 'Disabled', value: false }
            ]
        },
        activeSelectedClouds() {
            if (Array.isArray(this.selectedClouds)) {
                return this.selectedClouds
            }
            return this.cloudFilterOptions
        },
        activeSelectedRegions() {
            if (Array.isArray(this.selectedRegions)) {
                return this.selectedRegions
            }
            return this.regionFilterOptions
        },
        activeSelectedEnabledStates() {
            if (Array.isArray(this.selectedEnabledStates)) {
                return this.selectedEnabledStates
            }
            return this.enabledFilterOptions.map(item => item.value)
        },
        pricesBySubresource() {
            return this.prices.reduce((accumulator, priceEntry) => {
                const subresource = priceEntry?.subresource
                if (!subresource) return accumulator
                if (!Array.isArray(accumulator[subresource])) {
                    accumulator[subresource] = []
                }
                accumulator[subresource].push(priceEntry)
                return accumulator
            }, {})
        },
        filteredNodePools() {
            return this.nodePools.filter(nodePool => {
                const hasCloudFilters = this.cloudFilterOptions.length > 0
                const hasRegionFilters = this.regionFilterOptions.length > 0
                const hasEnabledFilters = this.enabledFilterOptions.length > 0
                const isEnabled = !nodePool.disabled_override
                const matchesCloud = !hasCloudFilters || this.activeSelectedClouds.includes(nodePool.cloud)
                const matchesRegion = !hasRegionFilters || this.activeSelectedRegions.includes(nodePool.region)
                const matchesEnabled = !hasEnabledFilters || this.activeSelectedEnabledStates.includes(isEnabled)
                return matchesCloud && matchesRegion && matchesEnabled
            })
        }
    },
    methods: {
        emptyNodePoolForm() {
            return {
                long_id: '',
                short_id: '',
                description: '',
                cloud: 'azure',
                region: 'westeurope',
                cloud_sku: 'custom',
                cpu: '',
                memory: ''
            }
        },
        required(value) {
            return Boolean(String(value ?? '').trim()) || 'Required'
        },
        positive(value) {
            return Number(value) > 0 || 'Enter an amount greater than zero'
        },
        notify(text, icon = 'check_circle') {
            this.$store.dispatch('showSnackBar', { snackBarText: text, snackBarTimeout: 5000, snackBarIcon: icon })
        },
        normalizeIntegrations(integrationsResponse) {
            if (Array.isArray(integrationsResponse)) return integrationsResponse
            if (integrationsResponse && typeof integrationsResponse === 'object') return [integrationsResponse]
            return []
        },
        integrationLabel(integration) {
            return integration.cluster_name || integration.cloud_provider || `Integration ${integration.tcid}`
        },
        integrationDetails(integration) {
            return [integration.cloud_provider, integration.cloud_region].filter(Boolean).join(' · ')
        },
        normalizeCloudProviders(cloudProvidersResponse) {
            if (Array.isArray(cloudProvidersResponse)) {
                return cloudProvidersResponse.map(provider => ({
                    cloud_provider: provider.cloud_provider,
                    display_label: provider.display_label || provider.cloud_provider,
                    regions: Array.isArray(provider.regions) ? provider.regions : []
                }))
            }

            if (cloudProvidersResponse && typeof cloudProvidersResponse === 'object') {
                return Object.entries(cloudProvidersResponse).map(([cloudProvider, regions]) => ({
                    cloud_provider: cloudProvider,
                    display_label: cloudProvider,
                    regions: Array.isArray(regions) ? regions : []
                }))
            }

            return []
        },
        async fetchCloudProviders() {
            this.loadingCloudProviders = true
            try {
                const { data } = await this.$axios.get('/tenant_clusters/cloud_providers')
                this.cloudProviders = this.normalizeCloudProviders(data?.cloud_providers)

                if (!this.cloudProviderOptions.find(provider => provider.cloud_provider === this.nodePoolForm.cloud)) {
                    this.nodePoolForm.cloud = this.cloudProviderOptions[0]?.cloud_provider || ''
                }

                if (!this.cloudRegionOptions.includes(this.nodePoolForm.region)) {
                    this.nodePoolForm.region = this.cloudRegionOptions[0] || ''
                }
            } catch (error) {
                this.cloudProviders = []
                this.error = error.response?.data?.debug || 'Could not load providers.'
            } finally {
                this.loadingCloudProviders = false
            }
        },
        nodePoolPrices(shortId) {
            return this.pricesBySubresource[shortId] || []
        },
        nodePoolPriceAmount(shortId) {
            const prices = this.nodePoolPrices(shortId)
            if (prices.length === 0) return '-'

            const preferredPrice = prices.find(priceEntry => Number(priceEntry.resource_type) === 100 && Number(priceEntry.denomination) === 1)

            return preferredPrice?.amount ?? prices[0]?.amount ?? '-'
        },
        setCloudSelection(cloud, selected) {
            const nextSelectedClouds = [...this.activeSelectedClouds]
            const cloudIndex = nextSelectedClouds.indexOf(cloud)

            if (selected && cloudIndex === -1) {
                nextSelectedClouds.push(cloud)
            }

            if (!selected && cloudIndex !== -1) {
                nextSelectedClouds.splice(cloudIndex, 1)
            }

            this.selectedClouds = nextSelectedClouds
        },
        setRegionSelection(region, selected) {
            const nextSelectedRegions = [...this.activeSelectedRegions]
            const regionIndex = nextSelectedRegions.indexOf(region)

            if (selected && regionIndex === -1) {
                nextSelectedRegions.push(region)
            }

            if (!selected && regionIndex !== -1) {
                nextSelectedRegions.splice(regionIndex, 1)
            }

            this.selectedRegions = nextSelectedRegions
        },
        setEnabledSelection(value, selected) {
            const nextSelectedStates = [...this.activeSelectedEnabledStates]
            const valueIndex = nextSelectedStates.indexOf(value)

            if (selected && valueIndex === -1) {
                nextSelectedStates.push(value)
            }

            if (!selected && valueIndex !== -1) {
                nextSelectedStates.splice(valueIndex, 1)
            }

            this.selectedEnabledStates = nextSelectedStates
        },
        async fetchIntegrations() {
            if (!this.resourcePoolId) return
            this.loadingIntegrations = true
            try {
                const { data } = await this.$axios.get(`/resource_pools/${this.resourcePoolId}/tenant_cluster`)
                this.integrations = this.normalizeIntegrations(data).filter(integration => Boolean(integration.tcid))
            } catch (error) {
                if (error.response?.status === 404) {
                    this.integrations = []
                    return
                }
                this.integrations = []
                this.error = error.response?.data?.debug || 'Could not load integrations.'
            } finally {
                this.loadingIntegrations = false
            }
        },
        async refreshIntegrationResources(integration) {
            if (!integration?.tcid || this.refreshingResources) return
            this.refreshingResources = true
            this.error = ''
            try {
                await this.$axios.post(`/resource/${this.resourcePoolId}/tenant_clusters/${integration.tcid}/resync`)
                this.notify(`Resources refreshed for ${this.integrationLabel(integration)}.`)
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not refresh resources.'
            } finally {
                this.refreshingResources = false
            }
        },
        async fetchNodePools() {
            if (!this.resourcePoolId) return
            this.loading = true
            this.error = ''
            try {
                const { data } = await this.$axios.get(`/resource_pools/${this.resourcePoolId}/node_pools`)
                this.nodePools = Array.isArray(data) ? data : []

                const { data: pricesData } = await this.$axios.get(`/resource_pools/${this.resourcePoolId}/prices`)
                this.prices = Array.isArray(pricesData) ? pricesData : []
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not load resources or prices.'
            } finally {
                this.loading = false
            }
        },
        openCreateDialog() {
            this.editingNodePool = null
            this.nodePoolForm = this.emptyNodePoolForm()
            this.nodePoolDialog = true
            this.fetchCloudProviders()
        },
        openEditDialog(nodePool) {
            this.editingNodePool = nodePool
            this.nodePoolForm = {
                long_id: nodePool.long_id || '',
                short_id: nodePool.short_id || '',
                description: nodePool.description || '',
                cloud: nodePool.cloud || 'azure',
                region: nodePool.region || 'westeurope',
                cloud_sku: nodePool.cloud_sku || 'custom',
                cpu: nodePool?.limits?.cpu || '',
                memory: nodePool?.limits?.memory || ''
            }
            this.nodePoolDialog = true
        },
        closeNodePoolDialog() {
            this.nodePoolDialog = false
            this.editingNodePool = null
        },
        async saveNodePool() {
            if (!this.$refs.nodePoolForm.validate()) return
            this.saving = true
            try {
                const payload = {
                    description: this.nodePoolForm.description || null,
                    limits: {
                        cpu: String(this.nodePoolForm.cpu || '').trim(),
                        memory: String(this.nodePoolForm.memory || '').trim()
                    }
                }
                if (this.editingNodePool) {
                    await this.$axios.patch(`/resource_pools/${this.resourcePoolId}/node_pools/${this.editingNodePool.nid}`, payload)
                    this.notify('Resource updated.')
                } else {
                    await this.$axios.post(`/resource_pools/${this.resourcePoolId}/node_pools`, {
                        ...payload,
                        short_id: this.nodePoolForm.short_id,
                        long_id: this.nodePoolForm.long_id,
                        cloud: this.nodePoolForm.cloud || 'azure',
                        region: this.nodePoolForm.region || 'westeurope',
                        cloud_sku: this.nodePoolForm.cloud_sku || 'custom'
                    })
                    this.notify('Resource created.')
                }
                this.closeNodePoolDialog()
                await this.fetchNodePools()
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not save resource.'
            } finally {
                this.saving = false
            }
        },
        async setEnabled(nodePool, enabled) {
            try {
                await this.$axios.patch(`/resource_pools/${this.resourcePoolId}/node_pools/${nodePool.nid}`, { disabled_override: !enabled })
                nodePool.disabled_override = !enabled
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not update resource availability.'
            }
        },
        openPriceDialog(nodePool) {
            const existingPrice = this.nodePoolPrices(nodePool.short_id).find(
                priceEntry => Number(priceEntry.resource_type) === 100 && Number(priceEntry.denomination) === 1
            )
            this.price = {
                long_id: nodePool.long_id || '',
                subresource: nodePool.short_id,
                amount: existingPrice?.amount ?? null
            }
            this.priceDialog = true
        },
        async savePrice() {
            if (!this.$refs.priceForm.validate()) return
            this.savingPrice = true
            try {
                await this.$axios.put(`/resource_pools/${this.resourcePoolId}/prices`, {
                    resource_type: 100,
                    subresource: this.price.subresource,
                    denomination: 1,
                    amount: this.price.amount
                })
                this.priceDialog = false
                this.notify('Resource price updated.')
                await this.fetchNodePools()
            } catch (error) {
                this.error = error.response?.data?.debug || 'Could not update resource price.'
            } finally {
                this.savingPrice = false
            }
        }
    },
    watch: {
        resourcePoolId() {
            this.fetchNodePools()
            this.fetchIntegrations()
        },
        'nodePoolForm.cloud'(newCloudProvider) {
            if (!newCloudProvider) {
                this.nodePoolForm.region = ''
                return
            }
            if (!this.cloudRegionOptions.includes(this.nodePoolForm.region)) {
                this.nodePoolForm.region = ''
            }
        },
        nodePoolDialog(open) {
            if (!open && this.$refs.nodePoolForm) this.$refs.nodePoolForm.resetValidation()
        }
    },
    created() {
        this.fetchNodePools()
        this.fetchIntegrations()
    }
}
</script>

<style scoped>
@media (min-width: 960px) {
    .node-pool-filter-section--divider {
        border-left: 1px solid rgba(0, 0, 0, 0.12);
    }
}
</style>
